import { createEffect, createResource, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import type { CoworkArtifact } from "@/cowork/api"
import { ReportPreview } from "@/pages/session/v2/cowork-files-panel"

export function PublicReportPreview(props: {
  title: string
  format: string
  filename: string
  createdAt: string
  load: () => Promise<{ blob: Blob; filename?: string }>
}) {
  const language = useLanguage()
  const format = props.format.toLowerCase().replace(/^\./, "")
  const office = /^(xlsx|xlsm|excel|spreadsheet|workbook|docx|word|pptx|slides|presentation|powerpoint)$/.test(format)
  const text = /^(json|xml|csv|txt|text|md|markdown)$/.test(format)
  const [file] = createResource(async () => {
    const result = await props.load()
    return { ...result, text: text ? await result.blob.text() : undefined }
  })
  const [state, setState] = createStore({ url: "" })
  createEffect(() => {
    if (file.error) return
    const value = file()
    if (!value) return
    const url = URL.createObjectURL(value.blob)
    setState("url", url)
    onCleanup(() => URL.revokeObjectURL(url))
  })
  // Public links supply their own download without exposing private report metadata.
  const artifact: CoworkArtifact = {
    id: "public-report", company_id: "", project_id: "", artifact_type: "report", format,
    title: props.title, artifact_metadata: {}, report_status: "draft", validation_status: "incomplete",
    entity_scope: {}, source_coverage: {}, summary: {}, exceptions: [], validation_results: [], lineage: [],
    mapping_coverage_details: {}, status: "created", created_at: props.createdAt, updated_at: props.createdAt,
  }
  return (
    <section class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base" aria-label={props.title}>
      <Show when={!file.loading} fallback={
        <div role="status" class="grid min-h-60 flex-1 place-items-center text-13-regular text-v2-text-text-muted">
          {language.t("cowork.files.panel.loadingPreview")}
        </div>
      }>
        <Show when={!file.error} fallback={<p role="alert" class="p-6 text-13-regular">{language.t("cowork.report.share.downloadError")}</p>}>
          <Show when={file()} keyed>{(value) => (
            <>
              <div class="flex shrink-0 items-center justify-between gap-4 border-b border-v2-border-border-base px-4 py-3">
                <span class="min-w-0 truncate text-12-regular text-v2-text-text-muted">{props.filename}</span>
                <a class="shrink-0 rounded-md border border-v2-border-border-base px-3 py-1.5 text-12-medium hover:bg-v2-background-bg-layer-02"
                  href={state.url} download={value.filename || props.filename} rel="noreferrer">
                  {language.t("cowork.enterprise.reports.download")}
                </a>
              </div>
              <div class="min-h-0 flex-1 overflow-auto">
                <Show when={office || format === "csv"} fallback={
                  <Show when={text} fallback={
                    <iframe class="h-full min-h-[560px] w-full bg-white" src={state.url}
                      sandbox="allow-same-origin" referrerPolicy="no-referrer" title={props.title} />
                  }>
                    <pre class="whitespace-pre-wrap p-5 text-13-regular">{value.text}</pre>
                  </Show>
                }>
                  <ReportPreview artifact={artifact} load={async () => value} />
                </Show>
              </div>
            </>
          )}</Show>
        </Show>
      </Show>
    </section>
  )
}
